// ═══════════════════════════════════════════════════════════════════════════════
// LaborVarianceModal.jsx
//
// Drop this component into WorkOrderViewNew.jsx (or import it).
// It handles two modes for piecework WO labor items:
//
//   1. "qty_change"  — simple signed delta (positive add, negative reduction)
//   2. "rate_change" — close out remaining at old rate, open new item at new rate
//
// Props:
//   isOpen          : boolean
//   onClose         : () => void
//   onSuccess       : () => void   — called after successful save to trigger refetch
//   item            : the woItem row object from WorkOrderViewNew state
//   woId            : string
//   netQty          : number       — resolved net required qty (item.netRequiredQty ?? item.quantity)
//   qtyCharged      : number       — from piecework logs (item.qtyCharged / item.qtyOrdered)
//   qtyPending      : number       — from piecework logs (item.qtyPending)
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useMemo, useEffect } from "react";
import axios from "axios";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

export default function LaborVarianceModal({
  isOpen,
  onClose,
  onSuccess,
  item,
  woId,
}) {
  const [mode, setMode] = useState("qty_change"); // "qty_change" | "rate_change"
  const [deltaQty, setDeltaQty] = useState("");
  const [newRate, setNewRate] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // ── Derived from item ────────────────────────────────────────────────────
  const netQty = item ? (item.netRequiredQty ?? item.quantity ?? 0) : 0;
  const qtyCharged = item ? (item.qtyOrdered ?? item.qtyCharged ?? 0) : 0;
  const qtyPending = item ? (item.qtyPending ?? 0) : 0;
  const remaining = Math.max(0, netQty - qtyCharged - qtyPending);
  const currentRate = item ? item.unitRate || 0 : 0;

  // ── Reset on open ────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setMode("qty_change");
      setDeltaQty("");
      setNewRate("");
      setReason("");
      setError("");
    }
  }, [isOpen]);

  // ── Derived preview ──────────────────────────────────────────────────────
  const preview = useMemo(() => {
    if (!item) return null;

    if (mode === "qty_change") {
      const dq = parseFloat(deltaQty) || 0;
      if (dq === 0) return null;
      return {
        rows: [
          {
            label: "Qty delta",
            changeType: "qty_change",
            qty: dq,
            rate: currentRate,
            amount: dq * currentRate,
          },
        ],
        netQtyAfter: netQty + dq,
        costImpact: dq * currentRate,
      };
    }

    if (mode === "rate_change") {
      const nr = parseFloat(newRate) || 0;
      if (nr <= 0 || Math.abs(nr - currentRate) < 0.0001) return null;

      const rows = [];
      let costImpact = 0;

      // Closeout: only if there's remaining qty to close
      if (remaining > 0) {
        const closeoutAmt = -remaining * currentRate;
        rows.push({
          label: "Closeout at old rate",
          changeType: "price_change_closeout",
          qty: -remaining,
          rate: currentRate,
          amount: closeoutAmt,
        });
        costImpact += closeoutAmt;
      }

      // New rate item
      const newAmt = remaining * nr;
      rows.push({
        label: "New rate item",
        changeType: "price_change_new",
        qty: remaining,
        rate: nr,
        amount: newAmt,
      });
      costImpact += newAmt;

      return {
        rows,
        netQtyAfter: netQty, // net qty doesn't change for rate change
        costImpact,
        rateImpactPerUnit: nr - currentRate,
      };
    }

    return null;
  }, [
    mode,
    deltaQty,
    newRate,
    item,
    netQty,
    remaining,
    currentRate,
    qtyCharged,
    qtyPending,
  ]);

  // ── Validate ─────────────────────────────────────────────────────────────
  const validationError = useMemo(() => {
    if (mode === "qty_change") {
      const dq = parseFloat(deltaQty) || 0;
      if (dq === 0) return "Delta quantity must be non-zero.";
      if (dq < 0 && Math.abs(dq) > remaining)
        return `Cannot reduce by ${Math.abs(dq).toFixed(2)} — only ${remaining.toFixed(2)} remaining.`;
    }
    if (mode === "rate_change") {
      const nr = parseFloat(newRate) || 0;
      if (nr <= 0) return "New rate must be greater than 0.";
      if (Math.abs(nr - currentRate) < 0.0001)
        return "New rate is the same as current rate.";
      if (remaining === 0 && qtyCharged === 0 && qtyPending === 0)
        return "Item has no quantity to carry forward at the new rate.";
    }
    return "";
  }, [mode, deltaQty, newRate, remaining, currentRate, qtyCharged, qtyPending]);

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (validationError) return;
    if (!item) return;

    setSaving(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const body = {
        type: mode,
        assemblyId: item.assemblyId,
        reason: reason.trim() || undefined,
      };

      if (mode === "qty_change") {
        body.deltaQty = parseFloat(deltaQty);
      } else {
        body.newRate = parseFloat(newRate);
      }

      await axios.post(
        `${API_BASE}/workorders2/${woId}/items/${item.lineItemId}/labor-variance`,
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      onSuccess();
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to save variance. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !item) return null;

  const fmt = (n) =>
    `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtQty = (n) =>
    Number(n || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const changeTypeColor = (ct) => {
    if (ct === "price_change_closeout") return "text-red-400";
    if (ct === "price_change_new") return "text-green-400";
    if (ct === "qty_change")
      return parseFloat(deltaQty) > 0 ? "text-green-400" : "text-red-400";
    return "text-gray-300";
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8 text-center sm:p-0">
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-80 transition-opacity"
          onClick={onClose}
        />

        <div className="relative inline-block w-full max-w-2xl text-left bg-gray-800 rounded-xl shadow-2xl border border-gray-600 overflow-hidden">
          {/* ── Header ── */}
          <div className="flex items-start justify-between px-6 py-5 border-b border-gray-700">
            <div>
              <h3 className="text-lg font-semibold text-white">
                Labor Item Variance
              </h3>
              <p
                className="text-sm text-gray-400 mt-0.5 max-w-md truncate"
                title={item.description}
              >
                {item.conditionName || "—"} ·{" "}
                <span className="text-gray-300">{item.description}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl leading-none ml-4 mt-0.5"
            >
              ×
            </button>
          </div>

          {/* ── Current state ── */}
          <div className="px-6 py-4 bg-gray-900/60 border-b border-gray-700">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">
                  Net Required
                </div>
                <div className="font-semibold text-white">
                  {fmtQty(netQty)} {item.uom || ""}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">
                  Charged (approved)
                </div>
                <div className="font-semibold text-green-400">
                  {fmtQty(qtyCharged)}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">
                  Pending
                </div>
                <div className="font-semibold text-yellow-400">
                  {fmtQty(qtyPending)}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">
                  Remaining
                </div>
                <div
                  className={`font-semibold ${remaining > 0 ? "text-orange-400" : "text-gray-500"}`}
                >
                  {fmtQty(remaining)}
                </div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-700/60 grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">
                  Current Rate
                </div>
                <div className="font-semibold text-white">
                  {fmt(currentRate)} / {item.uom || "unit"}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">
                  Budget Amount
                </div>
                <div className="font-semibold text-orange-400">
                  {fmt(item.amount || netQty * currentRate)}
                </div>
              </div>
            </div>
          </div>

          {/* ── Mode toggle ── */}
          <div className="px-6 py-4">
            <div className="flex rounded-lg overflow-hidden border border-gray-600 mb-5">
              {[
                { value: "qty_change", label: "Add / Reduce Quantity" },
                { value: "rate_change", label: "Change Rate" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setMode(opt.value);
                    setDeltaQty("");
                    setNewRate("");
                    setError("");
                  }}
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                    mode === opt.value
                      ? "bg-orange-600 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* ── Qty change inputs ── */}
            {mode === "qty_change" && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">
                    Delta Quantity{" "}
                    <span className="text-gray-500 font-normal">
                      (positive = add, negative = reduce)
                    </span>
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      value={deltaQty}
                      onChange={(e) => setDeltaQty(e.target.value)}
                      step="0.01"
                      placeholder="e.g. 50 or -20"
                      className="w-40 px-3 py-2 bg-gray-700 border border-gray-500 rounded-lg text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none text-sm"
                    />
                    <span className="text-gray-400 text-sm">
                      {item.uom || ""}
                    </span>
                    {deltaQty && parseFloat(deltaQty) !== 0 && (
                      <span
                        className={`text-sm font-medium ${parseFloat(deltaQty) > 0 ? "text-green-400" : "text-red-400"}`}
                      >
                        {parseFloat(deltaQty) > 0 ? "+" : ""}
                        {fmtQty(parseFloat(deltaQty))} ={" "}
                        {fmt(parseFloat(deltaQty) * currentRate)}
                      </span>
                    )}
                  </div>
                  {remaining > 0 && (
                    <p className="mt-1.5 text-xs text-gray-500">
                      Max reduction:{" "}
                      <button
                        type="button"
                        className="text-orange-400 hover:text-orange-300 underline"
                        onClick={() => setDeltaQty(String(-remaining))}
                      >
                        -{fmtQty(remaining)}
                      </button>{" "}
                      (closes remaining)
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ── Rate change inputs ── */}
            {mode === "rate_change" && (
              <div className="space-y-4">
                {remaining === 0 && (qtyCharged > 0 || qtyPending > 0) && (
                  <div className="flex items-start gap-2.5 px-3 py-2.5 bg-amber-900/30 border border-amber-700/40 rounded-lg">
                    <svg
                      className="w-4 h-4 text-amber-400 mt-0.5 shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-sm text-amber-300">
                      All quantity has been charged or is pending. A rate change
                      here will create a zero-qty marker at the new rate for
                      record purposes — no quantity is moved.
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">
                    New Rate{" "}
                    <span className="text-gray-500 font-normal">
                      (per {item.uom || "unit"})
                    </span>
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">$</span>
                    <input
                      type="number"
                      value={newRate}
                      onChange={(e) => setNewRate(e.target.value)}
                      step="0.01"
                      min="0.01"
                      placeholder={currentRate.toFixed(2)}
                      className="w-40 px-3 py-2 bg-gray-700 border border-gray-500 rounded-lg text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none text-sm"
                    />
                    <span className="text-gray-400 text-sm">
                      / {item.uom || "unit"}
                    </span>
                    {newRate && parseFloat(newRate) > 0 && (
                      <span
                        className={`text-sm font-medium ml-2 ${parseFloat(newRate) > currentRate ? "text-red-400" : "text-green-400"}`}
                      >
                        {parseFloat(newRate) > currentRate ? "▲" : "▼"}{" "}
                        {fmt(Math.abs(parseFloat(newRate) - currentRate))} /
                        unit
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Reason ── */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Reason / Notes{" "}
                <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Field measurement revision, contract amendment..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-500 rounded-lg text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none text-sm"
              />
            </div>
          </div>

          {/* ── Preview ── */}
          {preview && (
            <div className="px-6 pb-4">
              <div className="rounded-lg border border-gray-600 overflow-hidden">
                <div className="px-4 py-2 bg-gray-900/60 border-b border-gray-700">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Variance Preview (delta items that will be created)
                  </span>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-800/50">
                      <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase font-medium">
                        Item
                      </th>
                      <th className="px-4 py-2 text-right text-xs text-gray-500 uppercase font-medium">
                        Qty Delta
                      </th>
                      <th className="px-4 py-2 text-right text-xs text-gray-500 uppercase font-medium">
                        Rate
                      </th>
                      <th className="px-4 py-2 text-right text-xs text-gray-500 uppercase font-medium">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/40">
                    {preview.rows.map((row, i) => (
                      <tr key={i} className="bg-gray-800/20">
                        <td className="px-4 py-2.5">
                          <span
                            className={`font-medium ${changeTypeColor(row.changeType)}`}
                          >
                            {row.label}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right font-medium text-white">
                          {row.qty > 0 ? "+" : ""}
                          {fmtQty(row.qty)}
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-300">
                          {fmt(row.rate)}
                        </td>
                        <td
                          className={`px-4 py-2.5 text-right font-semibold ${row.amount >= 0 ? "text-green-400" : "text-red-400"}`}
                        >
                          {row.amount >= 0 ? "" : "−"}
                          {fmt(Math.abs(row.amount))}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-900/60 font-semibold border-t border-gray-600">
                      <td
                        colSpan={3}
                        className="px-4 py-2.5 text-sm text-gray-300"
                      >
                        Net cost impact
                        {mode === "qty_change" && (
                          <span className="ml-3 text-xs font-normal text-gray-500">
                            New net qty:{" "}
                            <span className="text-white">
                              {fmtQty(preview.netQtyAfter)}
                            </span>
                          </span>
                        )}
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right ${preview.costImpact >= 0 ? "text-green-400" : "text-red-400"}`}
                      >
                        {preview.costImpact >= 0 ? "+" : "−"}
                        {fmt(Math.abs(preview.costImpact))}
                      </td>
                    </tr>
                  </tbody>
                </table>
                {mode === "rate_change" && qtyCharged > 0 && (
                  <div className="px-4 py-2.5 bg-gray-900/40 border-t border-gray-700/40 text-xs text-gray-500">
                    {fmtQty(qtyCharged)} already charged at {fmt(currentRate)}{" "}
                    will remain at original rate in history.
                    {qtyPending > 0 &&
                      ` ${fmtQty(qtyPending)} pending also stays at original rate.`}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Error ── */}
          {(error || validationError) && (
            <div className="mx-6 mb-4 px-4 py-2.5 bg-red-900/40 border border-red-700/50 rounded-lg text-sm text-red-300">
              {error || validationError}
            </div>
          )}

          {/* ── Actions ── */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-700 bg-gray-800/50">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-5 py-2 text-sm text-gray-200 bg-gray-600 rounded-lg hover:bg-gray-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving || !!validationError || !preview}
              className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors ${
                saving || !!validationError || !preview
                  ? "bg-orange-300 text-orange-100 cursor-not-allowed"
                  : "bg-orange-500 hover:bg-orange-600 text-white"
              }`}
            >
              {saving ? "Saving..." : "Save Variance"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
